import { css } from "@emotion/react";
import { Autocomplete } from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { useContext, useEffect, useMemo, useState } from "react";
import { createSearchParams, useNavigate } from "react-router";
import { pxToRem } from "../../design/utils";
import { trpc } from "../../utils/trpc";
import { SearchInput } from "../bits/search";
import { SearchContext } from "../contexts/WithSearch/SearchContext";
import { useQuery } from "../hooks/useQuery";
import { useUser } from "../hooks/useUser";
import { searchSuggestionsTitleMapping } from "../utils/searchSuggestionsTitleMapping";

type AutocompleteOption = {
  title?: string;
  category?: string;
  publicationId?: number;
};

const filter = createFilterOptions<AutocompleteOption>();

export const ShelfSearch = () => {
  const query = useQuery();
  const navigate = useNavigate();

  const { setSearchQuery, searchQuery } = useContext(SearchContext);
  const urlSearchQuery = query.get("search");
  const [searchInputValue, setSearchInputValue] = useState(
    urlSearchQuery ?? "",
  );
  const { user } = useUser();

  useEffect(() => {
    if (urlSearchQuery && urlSearchQuery !== searchInputValue) {
      setSearchInputValue(urlSearchQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearchQuery]);

  const { data: searchSuggestions, isLoading } =
    trpc.searchSuggestions.useQuery(
      {
        search: searchInputValue,
      },
      {
        enabled: searchInputValue.length > 0,
      },
    );

  const autocompleteOptions = useMemo<AutocompleteOption[]>(() => {
    if (searchInputValue.length > 0) {
      const searches = [
        ...new Set(
          searchSuggestions?.map((option) => ({
            title: option.searchTerm,
            category: searchSuggestionsTitleMapping[option.type],
          })) ?? [],
        ),
      ];
      return searches;
    } else {
      const searches = [
        ...new Set(
          user?.recentSearches
            .filter((option) => {
              return !option.publication;
            })
            .map((option) => ({
              title: option.query.trim(),
              category: "Recent searches",
              publicationId: option.publication,
            })) ?? [],
        ),
      ];
      return searches;
    }
  }, [searchInputValue.length, searchSuggestions, user?.recentSearches]);

  const updateQueryValue = (
    _: unknown,
    newValue: AutocompleteOption | null,
  ) => {
    const query =
      newValue?.category && newValue.category !== "Recent searches"
        ? `"${newValue.title?.trim()}"`
        : newValue?.title?.trim();

    setSearchQuery(query || "");

    navigate({
      pathname: "/search",
      ...(query
        ? { search: createSearchParams({ search: query }).toString() }
        : {}),
    });
  };

  return (
    <Autocomplete
      id="all-search"
      selectOnFocus
      options={autocompleteOptions ?? []}
      onChange={updateQueryValue}
      onInputChange={(e: unknown, newValue: string | null) => {
        if (e) {
          setSearchInputValue(newValue ?? "");
        }
      }}
      loading={searchInputValue.length > 0 && isLoading}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);
        return filtered || [];
      }}
      groupBy={(option) => option.category ?? ""}
      value={{ title: searchQuery }}
      getOptionLabel={(option) => option.title ?? ""}
      inputValue={searchInputValue}
      renderOption={(props, option) => (
        <li
          {...props}
          key={(option.title ?? "") + (option.publicationId ?? "")}
          // Allows reselecting option from suggestions to navigate to the search page
          onClick={(e) => {
            if (option === searchQuery) {
              updateQueryValue(null, option);
            }
            props.onClick?.(e);
          }}
        >
          {option.title}
        </li>
      )}
      renderInput={(params) => (
        <div ref={params.InputProps.ref}>
          <SearchInput
            css={({ breakpoints }) => css`
              width: 100%;
              height: ${pxToRem(40)};

              @media (max-width: ${breakpoints.values.md}px) {
              width: 195px;
            `}
            placeholder={`Search across ${user?.frjAccess ? "" : "your "}shelf${user?.frjAccess ? " & Financial Remedies Journal" : ""}`}
            type="text"
            {...params.inputProps}
            onKeyDown={(e) => {
              if (e.key === "Enter" && searchInputValue) {
                e.preventDefault();
                updateQueryValue(null, { title: searchInputValue });
              }
            }}
          />
        </div>
      )}
    />
  );
};
